import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { shouldPolyfill as shouldPolyfillCanonicalLocales } from '@formatjs/intl-getcanonicallocales/should-polyfill';
import { shouldPolyfill as shouldPolyfillIntlLocale } from '@formatjs/intl-locale/should-polyfill';
import { shouldPolyfill as shouldPolyfillPluralRules } from '@formatjs/intl-pluralrules/should-polyfill';

import ENV from 'receivable-invoices-js/config/environment';

const { defaultLocale: DEFAULT_LOCALE, supportedLocales: SUPPORTED_LOCALES } = ENV;

async function polyfillPluralRules(locale) {
  if (shouldPolyfillIntlLocale()) {
    await import('@formatjs/intl-locale/polyfill');
  }
  if (shouldPolyfillPluralRules()) {
    await import('@formatjs/intl-pluralrules/polyfill');
  }

  if (Intl.PluralRules.polyfilled) {
    switch (locale) {
      case 'de':
        await import('@formatjs/intl-pluralrules/locale-data/de');
        break;
      case 'en':
        await import('@formatjs/intl-pluralrules/locale-data/en');
        break;
      case 'es':
        await import('@formatjs/intl-pluralrules/locale-data/es');
        break;
      case 'fr':
        await import('@formatjs/intl-pluralrules/locale-data/fr');
        break;
      case 'it':
        await import('@formatjs/intl-pluralrules/locale-data/it');
        break;
      case 'nl':
        await import('@formatjs/intl-pluralrules/locale-data/nl');
        break;
    }
  }
}

async function getCanonicalLocalesPolyfill() {
  if (shouldPolyfillCanonicalLocales()) {
    await import('@formatjs/intl-getcanonicallocales/polyfill');
  }
}
/**
 * Returns formatted string into array
 *
 * @public
 * @method parseLocaleString
 *
 * @param  {String} string
 * @returns {Array} Parsed string
 */
export function parseLocaleString(string) {
  return string ? string.toLowerCase().split(/[-_]/) : [];
}

export default class LocalManager extends Service {
  @service intl;

  @tracked locale;

  /**
   * Get the browser language
   *
   * @public
   * @method getBrowserLocale
   * @returns {String} Browser language
   */
  getBrowserLocale() {
    return navigator.language;
  }

  /**
   * Returns supported browser local
   *
   * @public
   * @method getSupportedBrowserLocale
   * @returns {Array} Array of supported languages
   */
  getSupportedBrowserLocale() {
    let browserLocale = this.getBrowserLocale();
    if (SUPPORTED_LOCALES.includes(browserLocale)) {
      return browserLocale;
    }

    return DEFAULT_LOCALE;
  }

  /**
   * Returns true if the locale is supported
   *
   * @private
   * @method _isLocaleSupported
   * @param  {String} locale Locale to check
   * @returns {Boolean}
   */
  _isLocaleSupported(locale) {
    return SUPPORTED_LOCALES.includes(locale);
  }

  /**
   * Reads the locale browser and return the proper locale string
   *
   * @public
   * @method getSupportedLocale
   * @returns {String} Locale to use
   */
  getSupportedLocale() {
    let parsedBrowserLocale = parseLocaleString(this.getBrowserLocale())[0];

    if (this._isLocaleSupported(parsedBrowserLocale)) {
      return parsedBrowserLocale;
    }
    return this.getSupportedBrowserLocale();
  }

  /**
   * Fetch translation per language
   * Since we fetch all of them, they are grouped in the same chunk
   * @param {string} locale
   * @returns Promise<Translations>
   */
  async getLocaleTranslation(locale) {
    return await import(/* webpackChunkName: 'translation' */ `/translations/${locale}.json`);
  }

  /**
   * Add translations for the given locale
   *
   * @public
   * @method addLocaleTranslations
   */
  async addLocaleTranslations(locale) {
    this.intl.addTranslations(locale, await this.getLocaleTranslation(locale));
  }

  /**
   * Set the locale
   *
   * @public
   * @method setLocale
   *
   * @param  {String} locale Locale to set for the application
   * @returns void
   */
  async setLocale(locale = this.getSupportedLocale()) {
    try {
      // Load the polyfill 1st BEFORE loading the translations
      await Promise.all([polyfillPluralRules(locale), getCanonicalLocalesPolyfill()]);
    } catch {
      // there is not benefit in a network error
    }

    if (locale === this.locale) {
      return;
    }

    await this.addLocaleTranslations('en');
    await this.addLocaleTranslations('fr');
    await this.addLocaleTranslations('es');
    await this.addLocaleTranslations('de');
    await this.addLocaleTranslations('it');
    await this.addLocaleTranslations('nl');

    this.intl.setLocale(locale);
    this.locale = locale;
  }
}
